import React, { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Users, FileText, CheckCircle, Clock, Eye, Trash, FloppyDisk } from "@phosphor-icons/react";

const ADMIN_STATUSES = ["New", "Contacted", "Invoiced", "Paid", "Completed", "Cancelled"];
const STATUS_STYLES = {
  New: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Invoiced: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  Paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Completed: "bg-primary/15 text-primary border-primary/30",
  Cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [st, su] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/submissions"),
    ]);
    setStats(st.data);
    setSubs(su.data);
  };

  const updateStatus = async (id, admin_status) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/submissions/${id}`, { admin_status });
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, admin_status: data.admin_status } : s)));
      setDetail((d) => (d && d.id === id ? { ...d, admin_status: data.admin_status } : d));
      toast.success(`Status updated to ${admin_status}`);
    } catch {
      toast.error("Could not update status.");
    } finally {
      setSaving(false);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm("Delete this order permanently? This cannot be undone.")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/submissions/${id}`);
      setSubs((prev) => prev.filter((s) => s.id !== id));
      setDetail(null);
      await reload();
      toast.success("Order deleted.");
    } catch {
      toast.error("Could not delete order.");
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(async () => {
    const [st, su, sv] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/submissions"),
      api.get("/services"),
    ]);
    setStats(st.data);
    setSubs(su.data);
    setServices(sv.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const serviceMap = useMemo(
    () => Object.fromEntries(services.map((s) => [s.id, s])),
    [services]
  );
  const filtered = useMemo(
    () => subs.filter((s) => filter === "all" || s.service_id === filter),
    [subs, filter]
  );

  const statCards = stats
    ? [
        ["Customers", stats.customers, Users],
        ["Total templates", stats.total_submissions, FileText],
        ["In progress", stats.in_progress, Clock],
        ["Submitted", stats.submitted, CheckCircle],
      ]
    : [];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="overline text-primary mb-2">Admin</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter">Orders & Management</h1>
          <p className="text-sm text-muted-foreground mt-2">View every order, update its status, and manage records.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {statCards.map(([label, val, Icon]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-zinc-900/50 p-6" data-testid={`admin-stat-${label}`}>
              <Icon size={22} weight="duotone" className="text-primary mb-3" />
              <div className="font-display text-3xl tracking-tighter">{val}</div>
              <div className="text-sm text-muted-foreground mt-1">{label}</div>
            </div>
          ))}
        </div>

        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList className="bg-zinc-900/60 border border-white/10">
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
            {services.map((s) => (
              <TabsTrigger key={s.id} value={s.id} data-testid={`filter-${s.id}`}>
                {s.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="rounded-xl border border-white/10 bg-zinc-900/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-muted-foreground">
                <th className="px-5 py-3 font-medium">Customer</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Service</th>
                <th className="px-5 py-3 font-medium hidden lg:table-cell">Payment</th>
                <th className="px-5 py-3 font-medium">Order Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground" data-testid="no-submissions">No submissions yet.</td></tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`admin-row-${s.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium">{s.customer_name}</div>
                      <div className="text-xs text-muted-foreground">{s.customer_email}</div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <div>{s.service_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.status === "submitted" ? "Submitted" : `Step ${Math.min(s.current_step, s.total_steps)}/${s.total_steps}`}
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-muted-foreground">
                      {s.payment_method
                        ? <span>{s.payment_method}{s.amount_due != null ? ` · $${s.amount_due.toLocaleString()}` : ""}</span>
                        : <span className="text-xs">—</span>}
                    </td>
                    <td className="px-5 py-4">
                      <Select value={s.admin_status || "New"} onValueChange={(v) => updateStatus(s.id, v)}>
                        <SelectTrigger className="h-8 w-[140px] bg-zinc-900/60 border-white/10" data-testid={`status-select-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ADMIN_STATUSES.map((st) => (
                            <SelectItem key={st} value={st} data-testid={`status-opt-${s.id}-${st}`}>{st}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setDetail(s)} data-testid={`view-submission-${s.id}`}
                        className="text-primary hover:text-[#DBC197]">
                        <Eye size={16} className="mr-1" /> View
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteSubmission(s.id)} data-testid={`delete-submission-${s.id}`}
                        className="text-red-400 hover:text-red-300">
                        <Trash size={16} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-zinc-950 border-white/10">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {detail.service_name}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Submission details for {detail.customer_name}
                </DialogDescription>
                <p className="text-sm text-muted-foreground">
                  {detail.customer_name} · {detail.customer_email}
                </p>
                {detail.payment_method && (
                  <div className="mt-2 flex items-center gap-2 text-sm" data-testid="detail-payment">
                    <span className="rounded-md bg-primary/15 text-primary border border-primary/30 px-2 py-0.5">
                      Pay via {detail.payment_method}
                    </span>
                    {detail.amount_due != null && (
                      <span className="text-muted-foreground">
                        Amount due: <span className="text-foreground font-medium">${detail.amount_due.toLocaleString()}</span>
                      </span>
                    )}
                  </div>
                )}
              </DialogHeader>
              <div className="space-y-6 mt-2" data-testid="submission-detail">
                {(serviceMap[detail.service_id]?.steps || []).map((st) => (
                  <div key={st.title}>
                    <h3 className="font-display text-sm tracking-tight text-primary mb-2">{st.title}</h3>
                    <div className="space-y-2">
                      {st.fields.filter((f) => f.type !== "info" && f.type !== "link").map((f) => {
                        let v = detail.data[f.name];
                        if (Array.isArray(v)) v = v.join(", ");
                        return (
                          <div key={f.name} className="flex justify-between gap-4 text-sm border-b border-white/5 pb-2">
                            <span className="text-muted-foreground">{f.label}</span>
                            <span className="text-right">{v || "—"}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg border border-white/10 bg-zinc-900/40 p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Order status</span>
                    <Select value={detail.admin_status || "New"} onValueChange={(v) => updateStatus(detail.id, v)}>
                      <SelectTrigger className="h-9 w-[150px] bg-zinc-950 border-white/10" data-testid="detail-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ADMIN_STATUSES.map((st) => (
                          <SelectItem key={st} value={st}>{st}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Badge className={STATUS_STYLES[detail.admin_status] || STATUS_STYLES.New}>
                      {detail.admin_status || "New"}
                    </Badge>
                  </div>
                  <Button variant="ghost" disabled={saving} onClick={() => deleteSubmission(detail.id)}
                    data-testid="detail-delete-button" className="text-red-400 hover:text-red-300">
                    <Trash size={16} className="mr-1" /> Delete order
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
